import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import ShieldIcon from '@atlaskit/icon/core/shield';
import Skeleton from '@atlaskit/skeleton';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';

import { ContentTypeClassification } from '@confluence/data-classification/entry-points/ContentTypeClassification';
import { FlagStatus } from '@confluence/data-classification/entry-points/FlagStatus';
import { ContentClassificationTag } from '@confluence/data-classification/entry-points/ContentClassificationTag';
import { usePageContentId, useContentType, usePageSpaceKey } from '@confluence/page-context';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { ContentType } from '@confluence/content-types-utils/entry-points/contentTypeConstants';
import { fg } from '@confluence/feature-gating';

import { useContentClassification } from '../hooks/useContentClassification';

import { DetailRow } from './DetailRow';
import { InlineRetryError } from './InlineRetryError';
import { ContentClassifier } from './ContentClassifier';

const i18n = defineMessages({
	classificationSection: {
		id: 'details-panel.activity-section.classification-row',
		defaultMessage: 'Classification',
		description:
			"A title that appears next to the content's classification (highly confidential, confidential, internal, public)",
	},
});

export const handleRestoreFocus = (el?: HTMLElement) => {
	if (el) {
		el.focus();
	} else {
		const btn = document.querySelector('[data-testid="content-type-classification"]');
		if (btn) (btn as HTMLButtonElement).focus();
	}
};

export const ClassificationRow = () => {
	const { formatMessage } = useIntl();
	const [contentType] = useContentType();
	const [contentId] = usePageContentId();
	const [spaceKey] = usePageSpaceKey();
	const pageMode = useGetPageMode();
	const { data, error, loading, refetch } = useContentClassification();

	const shouldRenderContentClassificationTag =
		contentType === ContentType.PAGE &&
		(pageMode === PageMode.VIEW || data.flagStatus === FlagStatus.LOADING);

	const renderClassificationItem = () => {
		if (loading) {
			return (
				<UFOLoadHold name="classification-row-details-panel-hold">
					<Skeleton
						width="80px"
						height="24px"
						borderRadius={3}
						testId="classification-row-loading"
					/>
				</UFOLoadHold>
			);
		}

		if (error) {
			return <InlineRetryError error={error} refetch={refetch} />;
		}

		return shouldRenderContentClassificationTag ? (
			<>
				{fg('confluence_classification_row_content') ? (
					<ContentTypeClassification
						contentId={contentId ?? ''}
						contentType={contentType ?? ''}
						contentClassificationMetadata={data}
						spaceKey={spaceKey ?? ''}
						onRestoreFocus={handleRestoreFocus}
						buttonTestId="content-type-classification"
						spacing="expanded"
						readOnly
					/>
				) : (
					<ContentClassificationTag
						contentId={contentId ?? ''}
						contentType={contentType ?? ''}
						contentClassificationMetadata={data}
					/>
				)}
			</>
		) : (
			<ContentClassifier contentClassificationMetadata={data} />
		);
	};

	return (
		<DetailRow itemName={formatMessage(i18n.classificationSection)} icon={<ShieldIcon label="" />}>
			{renderClassificationItem()}
		</DetailRow>
	);
};
